import _ from "lodash";
import moment from "moment-timezone";

import SupabaseDocument from "data/supabase/document";
import { supabase } from "config/supabase";
import Timer from "../timer";
import CurrentSession from "data/userSession";
import { isValidString } from "utils/misc";
import TimeUtils from "utils/time";
import AnalyticsService from "./analytics";

export default class TugSession extends SupabaseDocument {
  static COLLECTION = "sessions";

  constructor(id = null, data = {}) {
    super();

    this.id = id;
    this.edited = false;

    this.timezone = _.get(data, "timezone", null);
    // Pin the session to the tz it was tugged in so its displayed time never
    // shifts when the user later travels. inThisTimezone() re-projects onto the
    // device tz, which is exactly the drift we don't want for a stored session.
    const dateInput = _.get(data, "date");
    this.date = this.timezone
      ? moment.tz(
          moment.isMoment(dateInput)
            ? dateInput.format(TimeUtils.FullDateTimeString)
            : dateInput,
          this.timezone
        )
      : moment(dateInput);
    this.date_original = this.date.clone();
    this._duration = _.get(data, "duration", 0);
    this._dateEnd = _.get(data, "dateEnd");
    this.exported = _.get(data, "exported", false);
    this.imported = _.get(data, "imported", false);
    this.inProgress = _.get(data, "inProgress", false);
    this.inProgressTime = _.get(data, "inProgressTime", null);
    this.method = _.get(data, "method", "");
    this.notes = _.get(data, "notes", "");
    this.sessions = _.get(data, "sessions", 1);
    this.uid = _.get(data, "uid");
    this.timer = new Timer(this.inProgressTime, this.timezone);
    // start/end/duration are bound by end = start + duration, so only two are
    // independent. We honor the two the user most-recently set and derive the
    // third. Most-recent-first; the last entry is the derived field. Default has
    // `end` derived, matching the stored model (date + _duration).
    this._editOrder = ["start", "duration", "end"];
    // Whether the user deliberately set the start time. startTimer() only
    // re-anchors the start to "now" when this is false (they opened the page and
    // left it a while before tugging); an explicit start is the source of truth.
    this._startTouchedByUser = false;
    // Check if original session had a time attached to it (default to true if new session)
    const timeRegex = /(\d{1,2}:\d{1,2}(:\d{1,2})?([ap]m)?)\b/gi;
    this.timeWasExplicitlySet =
      !!_.get(data, "date", "").match(timeRegex) || !this.id;

    this.saveCheckpoint();
  }

  // Build a TugSession from a Supabase `sessions` row.
  static fromRow(row) {
    if (!row) return null;
    // With a stored tz, project the instant into it. Without one (legacy rows),
    // the timestamp holds the original local wall clock stored as-is, so read its
    // UTC wall clock literally rather than shifting it into the device tz --
    // otherwise a 3:56am EST session shows as 9:56pm the day before in Central.
    const begin = row.timezone
      ? moment(row.started_at).tz(row.timezone)
      : moment.utc(row.started_at);
    // ponytail: re-derive the "time was explicit" bit from whether the start is
    // midnight in the session tz. A dedicated column would be exact, but a
    // date-only import reading as 00:00 is close enough to not warrant one.
    const isMidnight =
      begin.hour() === 0 && begin.minute() === 0 && begin.second() === 0;
    const dateStr = begin.format(
      isMidnight ? TimeUtils.FullDateString : TimeUtils.FullDateTimeString
    );
    // A resume time can never be in the future. Some legacy rows stored
    // in_progress_since with the session-tz offset applied twice, landing it
    // hours ahead of now -- which made the live timer count DOWN toward that
    // bogus instant. Fall back to started_at when that happens; it re-anchors
    // correctly on the next save.
    const resumeAt =
      row.in_progress_since && moment(row.in_progress_since).isAfter(moment())
        ? row.started_at
        : row.in_progress_since;
    return new TugSession(row.id, {
      date: dateStr,
      timezone: row.timezone,
      duration: row.duration_ms,
      exported: row.exported,
      imported: row.imported,
      inProgress: row.in_progress_since != null,
      // in_progress_since is a true instant; the Timer wants the app's "floating"
      // wall-clock epoch (clock-face time in the session tz, relabeled UTC), so
      // convert it back here. Keeps the column absolute + globally comparable.
      inProgressTime: resumeAt
        ? moment(resumeAt)
            .tz(row.timezone || TimeUtils.getTimezone())
            .utcOffset(0, true)
            .valueOf()
        : null,
      method: row.method,
      notes: row.notes,
      sessions: row.session_count,
      uid: row.uid,
    });
  }

  // snake_case row written to Supabase. id + uid are added by the base save().
  toRow() {
    this.timezone = this.timezone || TimeUtils.getTimezone();
    return {
      started_at: this.date.toISOString(),
      ended_at: this.inProgress ? null : this.end.toISOString(),
      duration_ms: this._duration,
      method: _.trim(this.method),
      session_count: this.sessions,
      notes: _.trim(this.notes),
      imported: this.imported,
      exported: this.exported,
      // Absolute instant the stopwatch is running since. This is the *resume*
      // time (inProgressTime), which can differ from the session start after a
      // pause/resume -- that difference is what drives the realign prompt, so it
      // must survive the round-trip. inProgressTime is a floating wall-clock epoch
      // (session-tz clock relabeled UTC); read its wall clock back and re-anchor
      // it in the session tz to get a true instant (the inverse of fromRow),
      // instead of the naive .valueOf() which would offset east-of-UTC users.
      in_progress_since: !this.inProgress
        ? null
        : this.inProgressTime != null
        ? moment
            .tz(
              moment
                .utc(this.inProgressTime)
                .format(TimeUtils.FullDateTimeString),
              this.timezone
            )
            .toISOString()
        : this.date.toISOString(),
      timezone: this.timezone,
    };
  }

  /** @returns {Promise<TugSession>} */
  static async fromID(ID) {
    const placeholder = new TugSession(ID);
    const { data } = await placeholder.fetch();

    if (!data) {
      throw new Error(`Session not found for ID: ${ID}`);
    }
    return TugSession.fromRow(data);
  }

  get changesMade() {
    // Ignore dateEnd (derived) when deciding whether a save is needed.
    const currentJson = _.cloneDeep(this.json);
    const originalJson = _.cloneDeep(this.json_original);
    delete currentJson.dateEnd;
    delete originalJson.dateEnd;
    return !_.isEqual(currentJson, originalJson);
  }

  get dateString() {
    return this.date.format("ddd MMM D");
  }

  get dateTimeString() {
    if (this.timeWasExplicitlySet) {
      return this.date.format("ddd MMM D @ h:mma");
    }
    return this.date.format("ddd MMM D");
  }

  // Same as dateTimeString but projected into the viewer's own timezone. Use when
  // showing another user's session so a time from a tz ahead of you reads as "X
  // ago" in your clock (e.g. their 4:49am shows as your 3:49am) instead of a
  // future-looking wall-clock time.
  get dateTimeStringLocal() {
    const local = this.date.clone().local();
    return this.timeWasExplicitlySet
      ? local.format("ddd MMM D @ h:mma")
      : local.format("ddd MMM D");
  }

  delete() {
    AnalyticsService.event("delete_tug_session");
    return super.delete();
  }

  get duration() {
    return this._duration + this.timer.elapsed;
  }

  get durationFinal() {
    if (this.inProgress) {
      return -1;
    }
    return this.duration;
  }

  get end() {
    return this.date.clone().add(this.duration, "milliseconds");
  }

  get completedAt() {
    if (!this.inProgress && this._dateEnd) {
      return TimeUtils.inThisTimezone(this._dateEnd.toDate(), this.timezone);
    }
    return this.end;
  }

  set end(date) {
    this._duration = date.diff(this.date);
  }

  // Record a manual edit to one of start/end/duration and recompute the third so
  // end = start + duration always holds, keeping the two values the user most
  // recently touched and deriving the stalest one. This is what makes "set timer
  // to 1min, then set start to 10min ago" keep the 1min duration and move end,
  // instead of silently rewriting the duration to 10min.
  // Returns false (and changes nothing) if the edit would make duration <= 0.
  _editTimeField(field, next) {
    // Snapshot all three before mutating; `next` overrides the edited one.
    const cur = {
      start: this.date.clone(),
      end: this.end,
      duration: this._duration,
    };
    cur[field] = next;

    const order = [field, ...this._editOrder.filter((f) => f !== field)];
    const derived = order[2];

    if (derived === "duration" && !cur.end.isAfter(cur.start)) {
      return false; // end before/at start -> non-positive duration; reject
    }

    this._editOrder = order;
    switch (derived) {
      case "end": // start & duration pinned
        this.date = cur.start;
        this._duration = cur.duration;
        break;
      case "duration": // start & end pinned
        this.date = cur.start;
        this._duration = cur.end.diff(cur.start);
        break;
      case "start": // end & duration pinned
        this.date = cur.end.clone().subtract(cur.duration, "milliseconds");
        this._duration = cur.duration;
        break;
      default:
        break;
    }
    return true;
  }

  setStart(start) {
    const ok = this._editTimeField(
      "start",
      moment.isMoment(start) ? start : moment(start)
    );
    // Only count it as a deliberate edit if it actually took -- a rejected edit
    // (e.g. start past a pinned end) leaves the start untouched.
    if (ok) {
      this._startTouchedByUser = true;
    }
    return ok;
  }

  setEnd(end) {
    return this._editTimeField("end", moment.isMoment(end) ? end : moment(end));
  }

  setDuration(ms) {
    return this._editTimeField("duration", ms);
  }

  // Sessions whose start falls within [start, end]. Grouped by start time; a
  // session spanning the boundary (rare -- sessions are minutes) counts to the
  // range it started in, not its span.
  static async between(uid, start, end, key) {
    const rows = await SupabaseDocument.query(
      TugSession.COLLECTION,
      (q) =>
        q
          .eq("uid", uid)
          .gte("started_at", start.toISOString())
          .lte("started_at", end.toISOString()),
      key
    );
    return rows.map((r) => TugSession.fromRow(r)).filter((s) => s !== null);
  }

  static async history(uid, month) {
    const start = moment(month).startOf("month");
    const end = moment(month).endOf("month");
    return TugSession.between(
      uid,
      start,
      end,
      `month ${start.format("YYYY-MM")} for ${uid}`
    );
  }

  // Sessions that OVERLAP today -- for the tug page's "today" total, which has no
  // use for the rest of the month. Can't filter on started_at alone: a session
  // begun before midnight and finished after it (11pm->1am) still contributes its
  // post-midnight slice today, so match on overlap instead -- started on/before
  // end-of-today AND ended on/after start-of-today. (The caller clips each
  // session's span to today's portion.) In-progress sessions (ended_at null) are
  // excluded; the only live one is the current session, tracked separately. Still
  // ~1/30th the rows of history(), so it stays cheap on a flaky / VPN link.
  static async today(uid) {
    const start = moment().startOf("day");
    const end = moment().endOf("day");
    const rows = await SupabaseDocument.query(
      TugSession.COLLECTION,
      (q) =>
        q
          .eq("uid", uid)
          .lte("started_at", end.toISOString())
          .gte("ended_at", start.toISOString()),
      `day ${start.format("YYYY-MM-DD")} for ${uid}`
    );
    return rows.map((r) => TugSession.fromRow(r)).filter((s) => s !== null);
  }

  get json() {
    // Plain, comparable snapshot used only for change detection.
    this.timezone = this.timezone || TimeUtils.getTimezone();
    return {
      date: this.date.format(
        this.timeWasExplicitlySet
          ? TimeUtils.FullDateTimeString
          : TimeUtils.FullDateString
      ),
      duration: this._duration,
      exported: this.exported,
      imported: this.imported,
      inProgress: this.inProgress,
      inProgressTime: this.inProgressTime,
      method: _.trim(this.method),
      notes: _.trim(this.notes),
      sessions: this.sessions,
      uid: this.uid,
      timezone: this.timezone,
    };
  }

  needsToBeExported() {
    this.exported = false;
  }

  // Flagged sessions for the Bugs page: stale-in-progress (>3 days) or
  // overlapping another of the user's sessions. Detection runs server-side
  // (session_issues RPC, filtered to this uid) so we only pull the small
  // affected set. Returns [{ session: TugSession, issues: string[] }].
  static async issues(uid) {
    // Through SupabaseDocument.rpc so it's memoized per uid, monitored (surfaces
    // if slow), and reset on any session write -- like the other reads here,
    // instead of a raw un-cached, un-monitored supabase.rpc.
    const data = await SupabaseDocument.rpc(
      "session_issues",
      { p_uid: uid },
      `issues ${uid}`
    );
    return data
      .map((row) => ({
        session: TugSession.fromRow(row.session),
        issues: row.issues || [],
        grp: row.grp, // overlap-cluster id; sessions in the same run share it
      }))
      .filter((x) => x.session);
  }

  static async recent(uid) {
    const rows = await SupabaseDocument.query(
      TugSession.COLLECTION,
      (q) =>
        q.eq("uid", uid).order("started_at", { ascending: false }).limit(10),
      `recent sessions for ${uid}`
    );
    return rows.map((r) => TugSession.fromRow(r)).filter((s) => s !== null);
  }

  static async getGlobalRecent(limitCount = 15, lastDoc = null) {
    const key = `global_recent_${lastDoc ? lastDoc.id : "head"}`;
    // `public_sessions` view excludes incognito/deleted users -- incognito means
    // invisible on the feed. Owner/profile reads still use the sessions table.
    const rows = await SupabaseDocument.query(
      "public_sessions",
      (q) => {
        let query = q.order("ended_at", {
          ascending: false,
          nullsFirst: false,
        });
        // keyset pagination on the ordered column
        if (lastDoc?.ended_at) query = query.lt("ended_at", lastDoc.ended_at);
        return query.limit(limitCount);
      },
      key
    );

    return {
      sessions: rows
        .map((r) => TugSession.fromRow(r))
        .filter((s) => s !== null),
      lastVisible: _.last(rows) || null, // raw row: carries id + ended_at cursor
    };
  }

  // Sessions currently in progress across ALL users, newest first. The feed shows
  // these as "X started Y N minutes ago" alongside completed ones. They have
  // ended_at = null, so getGlobalRecent (ordered by ended_at) sorts them off the
  // end and never surfaces them -- the feed fetches these separately and merges.
  // Match users_public.active (migration 0029): only sessions started in the last
  // 24h count as "active now", so an orphaned never-stopped session neither shows a
  // "Began" row nor lights the dot -- one rule, so the feed row and the pulse agree.
  // ponytail: 50 comfortably covers real concurrent tuggers (the 24h filter already
  // excludes the stale pile-up); newest-first so the freshest win the cap.
  static async getGlobalActive(limitCount = 15) {
    const since = moment().subtract(24, "hours").toISOString();
    // `public_sessions` view excludes incognito/deleted users (see getGlobalRecent).
    const rows = await SupabaseDocument.query(
      "public_sessions",
      (q) =>
        q
          .gt("in_progress_since", since)
          .order("in_progress_since", { ascending: false })
          .limit(limitCount),
      `global_active`
    );
    return rows.map((r) => TugSession.fromRow(r)).filter((s) => s !== null);
  }

  // Sessions currently in progress for a user (in_progress_since is set).
  static async active(uid) {
    const rows = await SupabaseDocument.query(
      TugSession.COLLECTION,
      (q) => q.eq("uid", uid).not("in_progress_since", "is", null).limit(1),
      `active from ${uid}`
    );
    return TugSession.fromRow(_.first(rows)) || undefined;
  }

  // Most recent session for a user (by start time).
  static async latest(uid) {
    const rows = await SupabaseDocument.query(
      TugSession.COLLECTION,
      (q) =>
        q.eq("uid", uid).order("started_at", { ascending: false }).limit(1),
      `latest for ${uid}`
    );
    return TugSession.fromRow(_.first(rows));
  }

  // Every distinct method this user has logged. Past methods used to live on the
  // user doc; now they're derived from the sessions table via a server-side distinct
  // (the user_methods RPC) so heavy users don't ship thousands of rows. Memoized per
  // uid and reset on any session write (SupabaseDocument.rpc / resetCache).
  static async distinctMethods(uid) {
    return SupabaseDocument.rpc(
      "user_methods",
      { p_uid: uid },
      `methods ${uid}`
    );
  }

  static async imported(uid) {
    const rows = await SupabaseDocument.query(
      TugSession.COLLECTION,
      (q) => q.eq("uid", uid).eq("imported", true),
      `imported for ${uid}`
    );
    return rows.map((r) => TugSession.fromRow(r)).filter((s) => s !== null);
  }

  static async pendingExportCount(uid) {
    const { count, error } = await supabase
      .from(TugSession.COLLECTION)
      .select("id", { count: "exact", head: true })
      .eq("uid", uid)
      .eq("exported", false);
    if (error) throw error;
    return count || 0;
  }

  get progressTimeMatchesStart() {
    const startFormatted = this.date
      .clone()
      .utcOffset(0, true)
      .format(TimeUtils.FullDateTimeString);
    const progressFormatted = moment(this.inProgressTime)
      .clone()
      .utc()
      .format(TimeUtils.FullDateTimeString);
    return startFormatted === progressFormatted;
  }

  reset() {
    this._duration = 0;
    this.timer.stop();
    this.inProgress = false;
    this.inProgressTime = null;
  }

  async save() {
    if (this.validationErrors.length) {
      throw _.first(this.validationErrors);
    }

    // Capture before super.save() overwrites json_original via saveCheckpoint()
    const wasInProgress = this.json_original?.inProgress || false;

    await super.save();
    AnalyticsService.event("save_tug_session", {
      inProgress: this.inProgress,
    });

    // Only update user.active if this session is/was in progress.
    // Prevents editing an old completed session from clearing active on the user doc.
    if (this.inProgress || wasInProgress) {
      CurrentSession.user.active = this.inProgress;
    }
    CurrentSession.user.started = this.date;
    CurrentSession.user.lastTugged = this.end;
    CurrentSession.user.cacheMethod(this.method);

    // Save user state to persist the 'active' property
    await CurrentSession.user.save();

    this.date_original = this.date.clone();
  }

  setProgressToStart() {
    if (this.inProgress) {
      this.inProgressTime = this.date.clone().utcOffset(0, true).valueOf();
      this._duration = 0;
      this.timer = new Timer(this.inProgressTime, this.timezone);
    }
  }

  startTimer() {
    if (this._startTouchedByUser) {
      // The user deliberately set a start time (e.g. they began tugging a few
      // minutes ago and are logging it now), so honor it as the source of truth
      // and run the stopwatch from that instant instead of re-anchoring to now.
      this._duration = 0;
      this.timer = new Timer(
        this.date.clone().utcOffset(0, true).valueOf(),
        this.timezone
      );
    } else {
      // The user never touched the start -- they likely opened the Tug page and
      // left it open a while before actually tugging -- so anchor start to now.
      if (this.duration === 0) {
        this.date = moment();
      }
      this.timer.start();
    }

    this.inProgress = true;
    this.inProgressTime = this.timer.startTime.valueOf();
    // Live run: start is fixed and duration accumulates, so end is the derived one.
    this._editOrder = ["duration", "start", "end"];
    // The anchor is locked in; a later resume should continue from the
    // accumulated duration unless the user edits the start again.
    this._startTouchedByUser = false;
  }

  stopTimer() {
    this._duration += this.timer.elapsed;

    this.timer.stop();
    this.inProgress = false;
    this.inProgressTime = null;
  }

  get validationErrors() {
    const errors = [];
    if (!this.date.isValid) {
      errors.push(new Error("Invalid date"));
    }
    if (!isValidString(this.method)) {
      errors.push(new Error("Invalid method name"));
    } else if (this.duration <= 0 && !this.inProgress) {
      errors.push(new Error("Tug duration must be set"));
    } else if (this.sessions < 1) {
      errors.push(new Error("Number of sessions should at least be 1"));
    } else if (!this.date.isValid()) {
      errors.push(new Error("Invalid date"));
    }
    return errors;
  }
}
